import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@shinetools/sunshine-universal';

import {
  ShineAccountingExportFormat,
  ShineRecurringAccountingExportSettingsCadence,
  ShineRecurringAccountingExportSettingsType,
} from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import BackButton from 'components/BackButton';
import useToast from 'components/ToastProvider/useToast';
import { AccountingExportForm } from 'features/Accounting/components/AccountingExportForm';
import {
  AccountingExportSchema,
  accountingExportSchema,
} from 'features/Accounting/components/AccountingExportForm/accountingExport.schema';
import * as Layout from 'layouts/ContentLayout';

import { ShineRecurringExportsDocument } from '../NewExportsHome/components/RecurringExportsList/graphql/shineRecurringExports.gql';
import { Confirmation } from './components/Confirmation';
import {
  CreateShineRecurringAccountingExportDocument,
  CreateShineRecurringAccountingExportMutation,
} from './graphql/createShineRecurringAccountingExport.gql';
import locales from './locales';

export const CreateExport = () => {
  const { companyProfileId } = useCompanyContext();
  const { showToast } = useToast();
  const [createdExportSetting, setCreatedExportSetting] = useState<
    | CreateShineRecurringAccountingExportMutation['createShineRecurringAccountingExportSettings']
    | null
  >(null);

  const form = useForm<AccountingExportSchema>({
    defaultValues: {
      cadence: ShineRecurringAccountingExportSettingsCadence.Monthly,
      cadenceOffset: 4,
      formats: [
        ShineAccountingExportFormat.Csv,
        ShineAccountingExportFormat.Ofx,
        ShineAccountingExportFormat.Qif,
      ],
      includeBankStatements: false,
      includeInvoices: true,
      includePersonalTransactions: false,
      recipients: [],
      type: ShineRecurringAccountingExportSettingsType.ShineRecurring,
    },
    resolver: zodResolver(accountingExportSchema),
  });

  const [
    createShineRecurringAccountingExport,
    { loading: createRecurringLoading },
  ] = useMutation(CreateShineRecurringAccountingExportDocument);

  const onSubmit: SubmitHandler<AccountingExportSchema> = async (data) => {
    const { type, ...input } = data;

    if (!companyProfileId) {
      showToast({ message: locales.errorMessage, type: 'error' });
      return;
    }

    if (type === ShineRecurringAccountingExportSettingsType.ShineRecurring) {
      await createShineRecurringAccountingExport({
        onCompleted: (response) =>
          setCreatedExportSetting(
            response.createShineRecurringAccountingExportSettings,
          ),
        onError: () =>
          showToast({ message: locales.errorMessage, type: 'error' }),
        refetchQueries: [
          {
            query: ShineRecurringExportsDocument,
            variables: { companyProfileId },
          },
        ],
        variables: { input: { companyProfileId, ...input } },
      });
    }
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Stack gap="space-32">
          <BackButton />

          {createdExportSetting ? (
            <Confirmation {...createdExportSetting} />
          ) : (
            <>
              <Typography.Header size="large">
                {locales.title}
              </Typography.Header>

              <AccountingExportForm form={form} />

              <Button
                alignSelf="flex-end"
                icon="calendar-next"
                isLoading={createRecurringLoading}
                onClick={form.handleSubmit(onSubmit)}
              >
                {locales.confirm}
              </Button>
            </>
          )}
        </Stack>
      </Layout.Body>
    </Layout.Root>
  );
};
